import { useState, useEffect } from "react";
import { HelpCircle, Copy, CheckCircle2, Mail, X } from "lucide-react";

export const SUPPORT_EMAIL = "contact@club-business-maroc.com";

export function HelpModal({ open, onClose }) {
  const [copied, setCopied] = useState(false);
  const [toast, setToast] = useState("");

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(""), 2000);
    return () => clearTimeout(t);
  }, [toast]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(SUPPORT_EMAIL);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = SUPPORT_EMAIL;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); } catch (_) { /* ignore */ }
      document.body.removeChild(ta);
    }
    setCopied(true);
    setToast("Email copié");
    setTimeout(() => setCopied(false), 2200);
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4 animate-fade-in"
      onClick={onClose}
      data-testid="help-modal"
    >
      <div
        className="cbm-card w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between p-5 border-b cbm-divider">
          <div className="flex items-center gap-2.5">
            <div
              className="w-9 h-9 rounded-lg flex items-center justify-center"
              style={{ background: "rgba(201,162,39,0.15)", border: "1px solid rgba(201,162,39,0.3)" }}
            >
              <HelpCircle size={16} className="text-gold" />
            </div>
            <div>
              <h2 className="font-outfit font-semibold text-white text-base leading-tight">Besoin d'aide</h2>
              <p className="text-cbm-muted text-xs mt-0.5">Nous sommes là pour vous aider</p>
            </div>
          </div>
          <button
            data-testid="close-help-modal"
            onClick={onClose}
            className="text-cbm-muted hover:text-white p-1 transition-colors"
            aria-label="Fermer"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <p className="text-sm leading-relaxed" style={{ color: "#CFD9EC" }}>
            Si vous avez besoin d'aide, vous pouvez nous contacter par email. Nous vous répondrons dans les plus brefs délais.
          </p>
          <p className="text-sm leading-relaxed" style={{ color: "#AFC3E0" }}>
            Merci de donner un maximum de détails dans votre message afin de vous aider efficacement.
          </p>

          <div>
            <label className="cbm-label block mb-1.5">Email de contact</label>
            <div className="flex gap-2">
              <input
                data-testid="help-email-input"
                readOnly
                value={SUPPORT_EMAIL}
                className="cbm-input flex-1 font-mono text-xs"
                onClick={(e) => e.target.select()}
              />
              <button
                data-testid="copy-help-email"
                onClick={copy}
                className={`cbm-btn-primary text-sm flex-shrink-0 px-3.5 ${copied ? "opacity-80" : ""}`}
              >
                {copied ? <><CheckCircle2 size={14} /> Copié</> : <><Copy size={14} /> Copier l'email</>}
              </button>
            </div>
          </div>

          <a
            data-testid="help-mailto"
            href={`mailto:${SUPPORT_EMAIL}?subject=Aide%20Club%20Business%20Maroc`}
            className="cbm-btn-secondary text-sm w-full justify-center py-2.5"
          >
            <Mail size={14} /> Ouvrir mon client mail
          </a>
        </div>
      </div>

      {toast && (
        <div
          data-testid="help-toast"
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[70] animate-fade-in-up"
        >
          <div
            className="px-4 py-2.5 rounded-lg flex items-center gap-2 shadow-xl"
            style={{
              background: "#1A2E5C",
              border: "1px solid rgba(201,162,39,0.4)",
              boxShadow: "0 12px 32px rgba(0,0,0,0.35)",
            }}
          >
            <CheckCircle2 size={15} className="text-gold" />
            <span className="text-white text-sm font-medium">{toast}</span>
          </div>
        </div>
      )}
    </div>
  );
}
